import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
	fetchMockInvitation,
	type InvitationError,
	type InvitationSuccess,
} from "@/mock/invitation.mock";
import { fetchInvitation } from "@/api/adapters/signup";

// Single global mock flag (same convention as every adapter): real BE when VITE_USE_MOCK=false.
const IS_MOCK = import.meta.env.VITE_USE_MOCK === "true";

type State =
	| { status: "loading" }
	| { status: "error"; error: InvitationError }
	| { status: "ready"; invitation: InvitationSuccess };

/**
 * Validates the invitation token from the URL query parameter.
 *
 * Reads `?token=...` from the current URL, calls the mock invitation
 * service, and returns one of three states:
 *
 * - `loading`  — token is being validated (show skeleton/spinner)
 * - `error`    — token is invalid/expired/registered (show error UI)
 * - `ready`    — token is valid, invitation data available (render form)
 *
 * In production, swap `fetchMockInvitation` with the real API call.
 */
export function useInvitationValidation(): State {
	const [searchParams] = useSearchParams();
	const token = searchParams.get("token") ?? "";
	const [state, setState] = useState<State>({ status: "loading" });

	useEffect(() => {
		let cancelled = false;

		async function validate() {
			setState({ status: "loading" });

			try {
				const result = IS_MOCK
					? await fetchMockInvitation(token)
					: await fetchInvitation(token);

				if (cancelled) return;

				if (result.valid) {
					setState({ status: "ready", invitation: result });
				} else {
					setState({ status: "error", error: result });
				}
			} catch {
				if (!cancelled) {
					setState({
						status: "error",
						error: { valid: false, reason: "server-error" },
					});
				}
			}
		}

		validate();

		return () => {
			cancelled = true;
		};
	}, [token]);

	return state;
}
